import React, { useCallback, useEffect, useState } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import searchIcon from "../../assets/icon/search-sm.svg";
import { Link, useNavigate } from "react-router-dom";
import HomeIcon from "../../assets/icon/home.svg";
import carIcon from "../../assets/icon/car.svg";
import bagIcon from "../../assets/icon/bag.svg";
import watchIcon from "../../assets/icon/watch.svg";
import apiService from "../../actions/apiService";
import Toast from "../../components/Toast";
import ConfirmTransactionModal from "../../components/models/ConfirmTransactionModal";

// Utility function to truncate text
const truncateText = (text, maxLength = 25) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const STATUS_PILL = {
  pending: "bg-[#FFC49F] text-[#D35100]",
  accepted: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  buyer_confirmed: "bg-[#FFC49F] text-[#D35100]",
  confirmed: "bg-[#B9FFCC] text-[#6C8F75]",
  released: "bg-[#B9FFCC] text-[#6C8F75]",
  disputed: "bg-red-100 text-red-700",
  resolved: "bg-blue-100 text-blue-700",
  cancelled: "bg-[#FF5040] text-[#ffffff]",
  payment_received: "bg-[#E0E0E0] text-[#575757]",
};

// Helper function to get status display text and pill key for buyers
const getStatusDisplay = (
  status,
  adminVerificationStatus,
  paymentCancelledReason,
  paymentInitiatedAt
) => {
  if (status === "cancelled") {
    if (paymentCancelledReason === "item_not_authentication") {
      return {
        text: "Cancelled",
        pillKey: "cancelled",
      };
    }
    return { text: "Cancelled", pillKey: "cancelled" };
  }
  // If payment was initiated (buyer clicked "I made the transfer"), show "Verification in progress"
  if (status === "pending" && paymentInitiatedAt) {
    return {
      text: "Verification in progress",
      smallText: "",
      pillKey: "payment_received",
    };
  }
  if (status === "pending") {
    return {
      text: "pending payment",
      smallText: "",
      pillKey: "pending",
    };
  }
  // After admin accepts payment (status="accepted" and admin_verification_status="verified"), show "Funds on hold"
  if (status === "accepted" && adminVerificationStatus === "verified") {
    return {
      text: "funds on hold",
      smallText: "",
      pillKey: "buyer_confirmed",
    };
  }
  if (status === "buyer_confirmed") {
    return {
      text: "funds on hold",
      smallText: "",
      pillKey: "buyer_confirmed",
    };
  }
  if (status === "confirmed") {
    return {
      text: "completed",
      pillKey: "confirmed",
      smallText: "",
    };
  }
  if (status === "released") {
    return { text: "completed", pillKey: "released" };
  }
  if (
    adminVerificationStatus === "released" ||
    adminVerificationStatus === "Released"
  ) {
    return { text: "completed", pillKey: "released" };
  }
  return {
    text: status.charAt(0).toUpperCase() + status.slice(1).replace("_", " "),
    pillKey: status,
  };
};

const BuyerMylistings = () => {
  const options = [
    { key: "car", label: "Cars", icon: carIcon },
    { key: "bag", label: "Bags", icon: bagIcon },
    { key: "watch", label: "Watches", icon: watchIcon },
    { key: "real_estate", label: "Real Estate", icon: HomeIcon },
  ];

  const [selected, setSelected] = useState("car");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [offerError, setOfferError] = useState("");
  const [sendingOffer, setSendingOffer] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [buyerFeePercentage, setBuyerFeePercentage] = useState(1);
  const [toastMessage, setToastMessage] = useState("");
  const [includeDeliveryFee, setIncludeDeliveryFee] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [deliveryFeeError, setDeliveryFeeError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [selectedItemForConfirm, setSelectedItemForConfirm] = useState(null);
  const navigate = useNavigate();

  const renderStatusPill = (item) => {
    // If current buyer already sent an offer that is still pending, show it
    const buyerRequestStatus =
      item?.buyerRequest?.status || item?.buyerRequestStatus;
    if (buyerRequestStatus === "pending") {
      return (
        <div className="justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full bg-[#5C31AC] text-[#ffffff]">
          Offer sent
        </div>
      );
    }

    // Incoming offer
    if (item?.pendingInvitation?.status === "pending") {
      return (
        <div className="justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full bg-[#8794E0] text-[#FFFFFF]">
          Offer received
        </div>
      );
    }

    // Use the same status determination logic as Transactions.js and TransDetails.js
    // Get the latest transaction from the transactions array
    const transactions = item?.transactions || [];
    const latestTransaction = transactions.length > 0 ? transactions[0] : null;

    // Determine status using the same logic as mapApiTransactionToRow in Transactions.js
    let status =
      latestTransaction?.status ||
      latestTransaction?.payment_status ||
      latestTransaction?.transfer_status ||
      latestTransaction?.admin_verification_status ||
      item?.status;
    status = (status ? String(status) : "pending").toLowerCase();

    // Get adminVerificationStatus and paymentCancelledReason from transaction
    const adminVerificationStatus = latestTransaction?.admin_verification_status
      ? String(latestTransaction.admin_verification_status).toLowerCase()
      : "";
    const paymentCancelledReason =
      latestTransaction?.payment_cancelled_reason || null;

    // Get payment_initiated_at from transaction
    const paymentInitiatedAt = latestTransaction?.payment_initiated_at || null;

    // Use getStatusDisplay to format the status
    const statusDisplay = getStatusDisplay(
      status,
      adminVerificationStatus,
      paymentCancelledReason,
      paymentInitiatedAt
    );

    return (
      <div className="flex flex-col gap-1">
        <span
          className={`justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full capitalize ${
            STATUS_PILL[statusDisplay.pillKey] ||
            STATUS_PILL[status] ||
            "bg-gray-100 text-gray-700"
          }`}
        >
          {statusDisplay.text}
        </span>
        {statusDisplay.smallText && (
          <span className="text-[12px] text-[#1E1E1E] opacity-50">
            {statusDisplay.smallText}
          </span>
        )}
      </div>
    );
  };

  const fetchItemsByCategory = useCallback(async () => {
    setLoading(true);
    setHasSearched(true); // Mark that we've searched
    const params = {
      page: 1,
      limit: 100,
      type: selected,
    };

    const res = await apiService.get("/items/buyer/items", params);
    setLoading(false);

    if (res.success) {
      setItems(res.data.items || []);
      // Store buyer fee percentage from API response
      if (res.data.buyerFeePercentage !== undefined) {
        setBuyerFeePercentage(res.data.buyerFeePercentage);
      }
    } else {
      console.error("Error fetching items:", res.error);
    }
  }, [selected]);

  const handleOpenOfferModal = async (item) => {
    setSelectedItem(item);
    setOfferError("");
    setOfferPrice("");
    setIncludeDeliveryFee(false);
    setDeliveryFeeError("");
    setDeliveryFee(null);
    setIsOfferOpen(true);
    // buyerFeePercentage is already set from the API response

    // Fetch delivery fee
    try {
      const configResult = await apiService.get("/transactions/delivery-fee");
      if (configResult.success && configResult.data?.delivery_fee) {
        setDeliveryFee(Number(configResult.data.delivery_fee));
        setDeliveryFeeError("");
      } else {
        setDeliveryFeeError("Failed to fetch delivery fee. Please try again.");
      }
    } catch (configError) {
      setDeliveryFeeError("Failed to fetch delivery fee. Please try again.");
      console.error("Failed to fetch delivery fee:", configError);
    }
  };

  const handleSubmitOffer = async (e) => {
    e?.preventDefault?.();
    if (!selectedItem) return;
    const amount = Number(offerPrice);
    if (!amount || amount <= 0 || Number.isNaN(amount)) {
      setOfferError("Please enter a valid offer price");
      return;
    }

    // Check if delivery fee is needed but not available
    if (selectedItem?.delivered_to_door && includeDeliveryFee && !deliveryFee) {
      setOfferError(
        "Delivery fee information is not available. Please try again."
      );
      return;
    }

    // Calculate total amount (offer price + service fee)
    // const serviceFeePercentage = buyerFeePercentage; // Use buyerFeePercentage from API response
    // const serviceFee = amount * (serviceFeePercentage / 100);
    const totalAmount = amount;

    setOfferError("");
    setSendingOffer(true);
    const payload = {
      item_id: selectedItem.id,
      recipient_user_id: selectedItem.seller_id,
      offer_amount: totalAmount, // Send total amount (offer + service fee)
      message: "",
      created_by: "buyer",
      is_delivered_door: includeDeliveryFee && selectedItem?.delivered_to_door,
    };
    const res = await apiService.post("/requests", payload);
    if (!res.success) {
      setOfferError(res.error || "Failed to send offer");
    } else {
      setIsOfferOpen(false);
      setOfferPrice("");
      setSelectedItem(null);
      setIncludeDeliveryFee(false);
      // Show success toast
      setToastMessage("Offer has been sent successfully");
      // Refresh items list
      fetchItemsByCategory();
    }
    setSendingOffer(false);
  };

  // Function to open confirmation modal
  const handleOpenConfirmModal = (item) => {
    setSelectedItemForConfirm(item);
    setModalError(null);
    setModalOpen(true);
  };

  // Function to confirm transaction
  const handleConfirmTransaction = async () => {
    if (!selectedItemForConfirm) return;

    const transactions = selectedItemForConfirm?.transactions || [];
    const latestTransaction = transactions.length > 0 ? transactions[0] : null;

    if (!latestTransaction?.id) {
      setModalError("Transaction not found");
      return;
    }

    setConfirmingId(latestTransaction.id);
    try {
      const response = await apiService.post(
        `/transactions/${latestTransaction.id}/confirm-buyer`
      );
      if (response?.success) {
        setToastMessage("Transaction confirmed successfully!");
        setModalOpen(false);
        setSelectedItemForConfirm(null);
        // Refresh items list
        if (hasSearched) {
          if (search) {
            handleSearch();
          } else {
            fetchItemsByCategory();
          }
        }
      } else {
        setModalError(response?.error || "Failed to confirm transaction");
      }
    } catch (err) {
      setModalError("Network error. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  // Function to close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalError(null);
    setSelectedItemForConfirm(null);
  };

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setHasSearched(true); // Mark that we've searched
    const params = {
      page: 1,
      limit: 100,
      search: search,
    };

    const res = await apiService.get("/items/buyer/items", params);
    setLoading(false);

    if (res.success) {
      setItems(res.data.items || []);
      // Store buyer fee percentage from API response
      if (res.data.buyerFeePercentage !== undefined) {
        setBuyerFeePercentage(res.data.buyerFeePercentage);
      }
    } else {
      console.error("Error fetching items:", res.error);
    }
  }, [search]);

  // Only fetch when category changes (not on initial mount)
  useEffect(() => {
    if (hasSearched) {
      fetchItemsByCategory();
    }
  }, [selected, fetchItemsByCategory, hasSearched]);

  const current = options.find((item) => item.key === selected);

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative z-[10]">
        <div className="flex px-5 items-center justify-between">
          <div>
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              Search Items
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Find cars, watches, Bags, and properties
            </p>
          </div>
        </div>

        <div className="mt-8 space-y-8 px-5 md:px-0">
          {/* Item search card */}
          <section className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
            <h2 className="text-big-price md:text-big-price-d font-[500] font-rubik text-[#1E1E1E]">
              Item search
            </h2>
            <p className="text-[16px] opacity-50 text-[#000025] mt-2 font-[400]">
              Search for items by name, model, or identifier accross all
              categories.
            </p>

            <div className="mt-8 flex flex-col gap-4">
              {/* Category select (mock) */}
              <div className="relative">
                {/* Dynamic icon based on selection */}
                <div className="absolute top-1/2 -translate-y-1/2 left-3 pointer-events-none">
                  <img
                    src={current.icon}
                    alt={current.label}
                    className="w-5 h-5 opacity-70"
                  />
                </div>

                {/* Dropdown */}
                <select
                  className="w-full ps-9 rounded-[8px] border border-[#00000026] px-[12px] py-[12px] text-[14px] text-[#1E1E1E]"
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                >
                  {options.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* Search row */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, model, or identifier..."
                    className="w-full rounded-[8px] border border-[#00000026]  px-[12px] py-[12px] placeholder:opacity-25 placeholder:text-[14px] text-[14px] placeholder:text-[#1E1E1E] text-[#1E1E1E]"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearch}
                  className="inline-flex gap-2 rounded-lg bg-blue-gradient max-w-[150px] w-full justify-center items-center px-[12px] py-[12px] text-[14px] font-[600] text-white "
                >
                  <img src={searchIcon} alt="" className="h-5 w-5 invert" />
                  Search
                </button>
              </div>
            </div>
          </section>

          {/* Show "How it works" when not searched yet */}
          {!hasSearched && (
            <section className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] font-rubik text-[#1E1E1E]">
                How item search works
              </h3>
              <p className="text-[16px] opacity-50 text-[#000025] mt-2 font-[400]">
                Find and purchase luxury items through our platform.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-3">
                {/* Step 1 */}
                <div className="rounded-[12px] border border-[#DCEDFF] p-5">
                  <div className="flex flex-col items-start gap-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M21 21L16.65 16.65M11 6C13.7614 6 16 8.23858 16 11M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                        stroke="#2A8BF7"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div>
                      <div className="font-[500] text-big-price md:text-big-price-d text-[#1E1E1E]">
                        Step 1: Search
                      </div>
                      <p className="mt-4 text-[16px] opacity-50 text-[#000025] font-[400]">
                        Search across cars, watches, jewelry, and properties
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="rounded-[12px] border border-[#DCEDFF] p-5">
                  <div className="flex flex-col items-start gap-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                    >
                      <path
                        d="M10.3334 11.5347C11.5669 10.8218 13.0997 10.8218 14.3333 11.5347M9.1618 9.17157C10.7239 10.7337 10.7239 13.2663 9.1618 14.8284C7.59971 16.3905 5.06705 16.3905 3.50494 14.8284C1.94285 13.2663 1.94285 10.7337 3.50494 9.17157C5.06704 7.60948 7.5997 7.60948 9.1618 9.17157ZM21.1618 9.17157C22.7239 10.7337 22.7239 13.2663 21.1618 14.8284C19.5997 16.3905 17.067 16.3905 15.5049 14.8284C13.9429 13.2663 13.9429 10.7337 15.5049 9.17157C17.067 7.60948 19.5997 7.60948 21.1618 9.17157Z"
                        stroke="#2A8BF7"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div>
                      <div className="font-[500] text-big-price md:text-big-price-d text-[#1E1E1E]">
                        Step 2: Review
                      </div>
                      <p className="mt-4 text-[16px] opacity-50 text-[#000025] font-[400]">
                        View detailed listing information and seller details
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="rounded-[12px] border border-[#DCEDFF] p-5">
                  <div className="flex flex-col items-start gap-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                    >
                      <path
                        d="M6.66663 16C6.66663 18.2091 8.45749 20 10.6666 20H14.6666C16.8758 20 18.6666 18.2091 18.6666 16C18.6666 13.7909 16.8758 12 14.6666 12H10.6666C8.45749 12 6.66663 10.2091 6.66663 8C6.66663 5.79086 8.45749 4 10.6666 4H14.6666C16.8758 4 18.6666 5.79086 18.6666 8M12.6666 2V22"
                        stroke="#2A8BF7"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div>
                      <div className="font-[500] text-big-price md:text-big-price-d text-[#1E1E1E]">
                        Step 3: Purchase
                      </div>
                      <p className="mt-4 text-[16px] opacity-50 text-[#000025] font-[400]">
                        Make secure payment through our escrow system
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Show search results when searched */}
          {hasSearched && (
            <section className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h3 className="text-big-price md:text-big-price-d font-[500] font-rubik text-[#1E1E1E]">
                Search results
              </h3>
              <p className="text-[16px] opacity-50 text-[#000025] mt-2 font-[400]">
                Find and purchase luxury items through our platform.
              </p>

              <div className="mt-8">
                {/* Search result item */}

                {loading ? (
                  <p className="opacity-50 text-center text-[#000025] font-[400]">
                    Loading...
                  </p>
                ) : items.length === 0 ? (
                  <p className="opacity-50 text-center text-[#000025] font-[400]">
                    No items found
                  </p>
                ) : (
                  <>
                    {items.map((item) => (
                      <>
                        <div
                          key={item.id}
                          className="flex flex-wrap items-center justify-between gap-8 p-2 px-5"
                        >
                          {/* Product details */}
                          <div className="flex justify-start sm:justify-center flex-wrap items-center flex-row gap-4">
                            {/* Product image */}
                            <div className="w-[100px] h-[83.962px] rounded-[8px] flex items-center justify-center bg-eslightBlue">
                              {item.images?.[0]?.file_url ? (
                                <img
                                  src={
                                    process.env.REACT_APP_IMAGE_URL +
                                    item.images[0].file_url
                                  }
                                  alt={item.title}
                                  className="object-cover w-full h-full rounded-[8px]"
                                />
                              ) : (
                                <span className="text-[#1E1E1E] text-xs font-bold">
                                  {item.category?.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col items-start justify-start gap-1">
                              <h4 className="text-[#1E1E1E] font-[600] text-[16px]">
                                {truncateText(item.title)}
                              </h4>
                              <span className="text-[14px] font-[600] opacity-75">
                                {item.custom_category
                                  ? item?.custom_category
                                  : item?.category}
                              </span>
                            </div>
                            <div className="flex ms-4 flex-col items-start justify-start gap-1">
                              {renderStatusPill(item)}
                              <div className="flex justify-center items-center gap-2">
                                <svg
                                  className="w-3 h-3 text-slate-400"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span className="text-[12px] text-[#1E1E1E] opacity-50 font-[500]">
                                  {item.location ? item.location : "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Price and actions */}
                          <div className="flex flex-col items-start sm:items-end gap-4">
                            {(() => {
                              const buyerReq = item?.buyerRequest;
                              const acceptedPrice =
                                buyerReq && buyerReq.status === "accepted"
                                  ? Number(buyerReq.offer_amount)
                                  : null;

                              const displayPrice = !acceptedPrice
                                ? Number(item.price) || 0
                                : acceptedPrice;

                              return (
                                <div className="flex flex-col items-start sm:items-end gap-1">
                                  <div className="text-[#2A8BF7] font-[600] text-big-price md:text-big-price-d">
                                    {displayPrice.toFixed(2)} €
                                  </div>
                                </div>
                              );
                            })()}
                            <div className="flex flex-col sm:flex-row gap-4">
                              {/* Show "See offer" button if there's a pending invitation (not rejected) */}
                              {item?.pendingInvitation?.status === "pending" ? (
                                <Link
                                  to={`/buyer/requests`}
                                  className="bg-blue-50 rounded-[8px] w-full text-center text-[14px] font-[600] min-w-[188px] text-esblue py-3 px-4 inline-flex items-center justify-center gap-2"
                                >
                                  See offer
                                </Link>
                              ) : (
                                <>
                                  <Link
                                    to={`/buyer/listings/${item.id}`}
                                    className="block bg-blue-50 rounded-[8px] w-full text-center text-[14px] font-[600] min-w-[188px] text-esblue py-3 px-4"
                                  >
                                    View details
                                  </Link>
                                  {(() => {
                                    // Use the same status determination logic as Transactions.js and TransDetails.js
                                    // Get the latest transaction from the transactions array
                                    const transactions =
                                      item?.transactions || [];
                                    const latestTransaction =
                                      transactions.length > 0
                                        ? transactions[0]
                                        : null;

                                    // Determine status using the same logic as mapApiTransactionToRow in Transactions.js
                                    let status =
                                      latestTransaction?.status ||
                                      latestTransaction?.payment_status ||
                                      latestTransaction?.transfer_status ||
                                      latestTransaction?.admin_verification_status ||
                                      item?.status;
                                    status = (
                                      status ? String(status) : "pending"
                                    ).toLowerCase();

                                    // Get adminVerificationStatus and paymentCancelledReason from transaction
                                    const adminVerificationStatus =
                                      latestTransaction?.admin_verification_status
                                        ? String(
                                            latestTransaction.admin_verification_status
                                          ).toLowerCase()
                                        : "";
                                    const paymentCancelledReason =
                                      latestTransaction?.payment_cancelled_reason ||
                                      null;

                                    // Get payment_initiated_at from transaction
                                    const paymentInitiatedAt =
                                      latestTransaction?.payment_initiated_at ||
                                      null;

                                    // Get buyer and seller confirmation status
                                    const buyerConfirmed = Boolean(
                                      latestTransaction?.buyer_confirmed
                                    );
                                    const sellerConfirmed = Boolean(
                                      latestTransaction?.seller_confirmed
                                    );

                                    // Use getStatusDisplay to format the status
                                    const statusDisplay = getStatusDisplay(
                                      status,
                                      adminVerificationStatus,
                                      paymentCancelledReason,
                                      paymentInitiatedAt
                                    );

                                    // Don't show any action if item is cancelled
                                    if (status === "cancelled") {
                                      return (
                                        <button
                                          disabled
                                          className="w-full bg-[#FF5040] text-white text-caption-d py-3 px-4 rounded-[8px] min-w-[188px] inline-flex items-center justify-center gap-2 cursor-not-allowed"
                                        >
                                          <span className="text-[14px] font-[600]">
                                            Cancelled
                                          </span>
                                        </button>
                                      );
                                    }

                                    // Show Confirm button for accepted/confirmed/buyer_confirmed status when buyer hasn't confirmed
                                    // Don't show for released or cancelled status
                                    const showConfirmButton =
                                      !buyerConfirmed &&
                                      status !== "cancelled" &&
                                      status !== "released" &&
                                      ([
                                        "accepted",
                                        "confirmed",
                                        "buyer_confirmed",
                                      ].includes(status) ||
                                        sellerConfirmed);

                                    if (showConfirmButton) {
                                      return (
                                        <button
                                          onClick={() =>
                                            handleOpenConfirmModal(item)
                                          }
                                          className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 px-4 rounded-[8px] min-w-[250px] inline-flex items-center justify-center gap-2"
                                        >
                                          <span>
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="18"
                                              height="13"
                                              viewBox="0 0 18 13"
                                              fill="none"
                                            >
                                              <path
                                                d="M17 1L6 12L1 7"
                                                stroke="#16653F"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </span>
                                          <span className="text-[14px] font-[600]">
                                            Confirm transaction done
                                          </span>
                                        </button>
                                      );
                                    }

                                    // Show "Proceed to payment" for pending status (when payment not yet initiated)
                                    if (
                                      status === "pending" &&
                                      !paymentInitiatedAt
                                    ) {
                                      return (
                                        <button
                                          onClick={() =>
                                            navigate(
                                              `/buyer/payment-initiate/${item.id}`
                                            )
                                          }
                                          className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 px-4 rounded-[8px] min-w-[250px] inline-flex items-center justify-center gap-2"
                                        >
                                          <span>
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="25"
                                              height="24"
                                              viewBox="0 0 25 24"
                                              fill="none"
                                            >
                                              <path
                                                d="M20.5 6L9.5 17L4.5 12"
                                                stroke="#16653F"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </span>
                                          <span className="text-[14px] font-[600]">
                                            Proceed to payment
                                          </span>
                                        </button>
                                      );
                                    }

                                    // Show status button based on statusDisplay
                                    if (
                                      statusDisplay.pillKey ===
                                      "payment_received"
                                    ) {
                                      return (
                                        <button
                                          disabled
                                          className="w-full bg-[#E0E0E0] text-[#575757] text-caption-d py-3 px-4 rounded-[8px] min-w-fit inline-flex items-center justify-center gap-2 cursor-not-allowed"
                                        >
                                          <span className="text-[14px] font-[600]">
                                            {statusDisplay.text}
                                          </span>
                                        </button>
                                      );
                                    }

                                    if (
                                      statusDisplay.pillKey ===
                                      "buyer_confirmed"
                                    ) {
                                      // If buyer already confirmed, show disabled "Funds on hold" button
                                      if (buyerConfirmed) {
                                        return (
                                          <button
                                            disabled
                                            className="w-full bg-[#FFC49F] text-[#D35100] text-caption-d py-3 px-4 rounded-[8px] min-w-[188px] inline-flex items-center justify-center gap-2 cursor-not-allowed"
                                          >
                                            <span className="text-[14px] font-[600]">
                                              {statusDisplay.text}
                                            </span>
                                          </button>
                                        );
                                      }
                                    }

                                    if (
                                      statusDisplay.pillKey === "confirmed" ||
                                      statusDisplay.pillKey === "released"
                                    ) {
                                      return (
                                        <button
                                          disabled
                                          className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 px-4 rounded-[8px] min-w-[250px] inline-flex items-center justify-center gap-2 cursor-not-allowed"
                                        >
                                          <span className="text-[14px] font-[600]">
                                            {statusDisplay.text}
                                          </span>
                                        </button>
                                      );
                                    }

                                    if (statusDisplay.pillKey === "pending") {
                                      return (
                                        <button
                                          onClick={() =>
                                            navigate(
                                              `/buyer/payment-initiate/${item.id}`
                                            )
                                          }
                                          className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 px-4 rounded-[8px] min-w-[250px] inline-flex items-center justify-center gap-2"
                                        >
                                          <span>
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="25"
                                              height="24"
                                              viewBox="0 0 25 24"
                                              fill="none"
                                            >
                                              <path
                                                d="M20.5 6L9.5 17L4.5 12"
                                                stroke="#16653F"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </span>
                                          <span className="text-[14px] font-[600]">
                                            Proceed to payment
                                          </span>
                                        </button>
                                      );
                                    }

                                    // Check for pending offer
                                    const buyerRequestStatus =
                                      item?.buyerRequest?.status ||
                                      item?.buyerRequestStatus;
                                    if (buyerRequestStatus === "pending") {
                                      return (
                                        <button
                                          className="w-full bg-[#5C31AC] text-white text-caption-d py-3 px-4 rounded-[8px] min-w-[188px] inline-flex items-center justify-center gap-2"
                                          disabled
                                        >
                                          <span className="text-[14px] font-[600]">
                                            Offer sent
                                          </span>
                                        </button>
                                      );
                                    }

                                    // Default: Show "Make offer" button
                                    return (
                                      <button
                                        onClick={() =>
                                          handleOpenOfferModal(item)
                                        }
                                        className="w-full bg-blue-gradient text-white text-caption-d py-3 px-4 rounded-[8px] min-w-[188px] inline-flex items-center justify-center gap-2"
                                      >
                                        <span>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="25"
                                            height="24"
                                            viewBox="0 0 25 24"
                                            fill="none"
                                          >
                                            <path
                                              d="M6.5 16C6.5 18.2091 8.29086 20 10.5 20H14.5C16.7091 20 18.5 18.2091 18.5 16C18.5 13.7909 16.7091 12 14.5 12H10.5C8.29086 12 6.5 10.2091 6.5 8C6.5 5.79086 8.29086 4 10.5 4H14.5C16.7091 4 18.5 5.79086 18.5 8M12.5 2V22"
                                              stroke="white"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </span>
                                        <span className="text-[14px] font-[600]">
                                          Make offer
                                        </span>
                                      </button>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    ))}
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Offer Modal */}
      {isOfferOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setIsOfferOpen(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-[12px] p-5 md:p-8 max-w-[625px] w-full mx-4">
            {/* Header */}
            <h3 className="text-page-title md:text-page-title-d font-rubik text-gray-[#1E1E1E] mb-4">
              Send offer
            </h3>

            {/* Content */}
            <form onSubmit={handleSubmitOffer}>
              <div className="mb-8">
                <label
                  className="block text-[14px] font-[500] text-[#1E1E1E] mb-2"
                  htmlFor="offerPrice"
                >
                  Offer price
                </label>
                <input
                  id="offerPrice"
                  type="number"
                  step="0.01"
                  placeholder="Enter offer price"
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value)}
                  className="w-full border border-[#E8F2FF] rounded-[8px] px-3 py-2 outline-none focus:ring-2 focus:ring-esblue"
                />
                {offerError && (
                  <div className="text-red-500 text-[13px] mt-2">
                    {offerError}
                  </div>
                )}
                {offerPrice &&
                  !isNaN(Number(offerPrice)) &&
                  Number(offerPrice) > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-[14px] text-[#1E1E1E]">
                        <span className="opacity-75">
                          Service fee ({buyerFeePercentage}%)
                        </span>
                        <span className="font-[500]">
                          {(
                            (Number(offerPrice) * buyerFeePercentage) /
                            100
                          ).toFixed(2)}{" "}
                          €
                        </span>
                      </div>
                      {selectedItem?.delivered_to_door &&
                        includeDeliveryFee &&
                        deliveryFee && (
                          <div className="flex justify-between text-[14px] text-[#1E1E1E]">
                            <span className="opacity-75">Delivery fee</span>
                            <span className="font-[500]">
                              {deliveryFee.toFixed(2)} €
                            </span>
                          </div>
                        )}
                      <div className="flex justify-between text-[16px] font-[600] text-[#1E1E1E] border-t border-[#E8F2FF] pt-2">
                        <span>Total</span>
                        <span>
                          {(
                            Number(offerPrice) +
                            (Number(offerPrice) * buyerFeePercentage) / 100 +
                            (selectedItem?.delivered_to_door &&
                            includeDeliveryFee &&
                            deliveryFee
                              ? deliveryFee
                              : 0)
                          ).toFixed(2)}{" "}
                          €
                        </span>
                      </div>
                    </div>
                  )}
              </div>

              {/* Delivery Fee Checkbox - Only show if delivered_to_door is true */}
              {selectedItem?.delivered_to_door && (
                <div className="mb-8">
                  {deliveryFeeError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-[13px]">
                        {deliveryFeeError}
                      </p>
                    </div>
                  )}
                  <div
                    onClick={() => {
                      if (!deliveryFee && !deliveryFeeError) {
                        setDeliveryFeeError(
                          "Delivery fee information is not available. Please close and reopen the modal."
                        );
                        return;
                      }
                      if (deliveryFee) {
                        setIncludeDeliveryFee(!includeDeliveryFee);
                      }
                    }}
                    className={`flex flex-col md:flex-row md:items-center md:justify-between p-2 rounded-lg border-2 cursor-pointer transition-all ${
                      includeDeliveryFee
                        ? "bg-green-50 border-green-500"
                        : deliveryFeeError || !deliveryFee
                        ? "bg-gray-50 border-gray-300 opacity-60"
                        : "bg-white border-gray-200 hover:border-esblue"
                    } ${
                      !deliveryFee && !deliveryFeeError
                        ? "cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <div className="flex-1 flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          includeDeliveryFee
                            ? "bg-green-500 border-green-500"
                            : "border-gray-300"
                        }`}
                      >
                        {includeDeliveryFee && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="text-[12px] text-gray-500 mb-0">
                          Authenticate and get it delivered to your door
                        </p>
                        {deliveryFee ? (
                          <p
                            className={`text-[18px] font-[600] ${
                              includeDeliveryFee
                                ? "text-green-600"
                                : "text-[#1E1E1E]"
                            }`}
                          >
                            {deliveryFee.toLocaleString()}€
                          </p>
                        ) : (
                          <p className="text-[18px] font-[600] text-red-600">
                            Not available
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 md:mt-0 md:ml-4">
                      {includeDeliveryFee ? (
                        <span className="text-green-600 font-[600] text-[14px]">
                          Added
                        </span>
                      ) : (
                        <span className="text-esblue font-[600] text-[14px] flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          Add
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col md:flex-row gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => setIsOfferOpen(false)}
                  className="px-4 py-3 order-2 md:order-1 text-esblue min-w-[147px] rounded-[8px] bg-[#F5F8FF]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingOffer}
                  className="px-4 py-3 order-1 md:order-2 bg-blue-gradient min-w-[147px] text-white text-nav-d rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sendingOffer ? "Sending..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          type="success"
          onClose={() => setToastMessage("")}
        />
      )}

      {/* Confirmation Modal */}
      {(() => {
        if (!selectedItemForConfirm) return null;
        const transactions = selectedItemForConfirm?.transactions || [];
        const latestTransaction =
          transactions.length > 0 ? transactions[0] : null;
        return (
          <ConfirmTransactionModal
            isOpen={modalOpen}
            onClose={handleCloseModal}
            onConfirm={handleConfirmTransaction}
            transactionId={latestTransaction?.id}
            itemName={selectedItemForConfirm?.title}
            isLoading={confirmingId === latestTransaction?.id}
            errorText={modalError}
          />
        );
      })()}
    </LayoutBuyerSeller>
  );
};

export default BuyerMylistings;
